"use client";

import AsyncAutocompleteSingleSelect from "@/components/Autocomplete/AsyncAutocompleteSingleSelect";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import toId from "@/utils/toId.utils";
import { FieldValues, useFormContext } from "react-hook-form";
import { AsyncAutocompleteFieldProps } from "./asyncAutocomplete.types";

/**
 * RHF wrapper around `AsyncAutocompleteSingleSelect`.
 *
 * Renders a labeled, accessible form field that fetches options from a server.
 * Must be rendered inside a shadcn `<Form>` provider.
 *
 * @example
 * <AsyncAutocompleteSingleSelectField
 *   name="assignee"
 *   label="Assignee"
 *   queryKey={["users"]}
 *   fetchOptions={userService.fetchUserOptions}
 *   defaultOption={user ? { value: user._id, label: user.name } : undefined}
 *   required
 * />
 */
function AsyncAutocompleteSingleSelectField<
  T extends FieldValues = FieldValues,
>({
  name,
  fetchOptions,
  queryKey,
  limit,
  debounce,
  defaultOption,
  label,
  description,
  required = false,
  placeholder,
  emptyMessage,
  disabled = false,
  showClear = true,
  value: externalValue,
  onValueChange,
  className,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}: AsyncAutocompleteFieldProps<T>) {
  const { control } = useFormContext<T>();

  const safeId = toId(name);
  const descriptionId = description ? `${safeId}-description` : undefined;
  const errorId = `${safeId}-error`;

  const {
    itemClass,
    labelClass,
    descriptionClass,
    messageClass,
    autocompleteClass,
  } = className ?? {};

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const ariaDescribedBy =
          [descriptionId, fieldState.error ? errorId : undefined]
            .filter(Boolean)
            .join(" ") || undefined;

        // externalValue overrides RHF (e.g. URL-synced forms).
        // RHF gives undefined for unset fields — normalize to null.
        const resolvedValue: string | null =
          externalValue !== undefined ? externalValue : (field.value ?? null);

        // Always call field.onChange to keep RHF in sync.
        // onValueChange is an optional side-effect (URL push, analytics, etc.)
        const handleValueChange = (selected: string | null) => {
          field.onChange(selected);
          onValueChange?.(selected);
        };

        return (
          <FormItem className={cn("space-y-0", itemClass)}>
            <FormLabel
              htmlFor={safeId}
              className={cn(
                "flex items-center gap-1 text-sm font-medium",
                !label && "sr-only",
                labelClass
              )}
            >
              <span>{label ?? name}</span>
              {required && (
                <span className="text-destructive" aria-hidden="true">
                  *
                </span>
              )}
            </FormLabel>

            {/*
             * Props are passed explicitly because AsyncAutocompleteSingleSelect
             * renders a custom Base UI input that FormControl cannot reach directly.
             */}
            <FormControl>
              <AsyncAutocompleteSingleSelect
                fetchOptions={fetchOptions}
                queryKey={queryKey}
                limit={limit}
                debounce={debounce}
                defaultOption={defaultOption}
                value={resolvedValue}
                onValueChange={handleValueChange}
                placeholder={placeholder}
                emptyMessage={emptyMessage}
                disabled={disabled}
                showClear={showClear}
                id={safeId}
                aria-label={ariaLabel ?? label}
                aria-labelledby={ariaLabelledBy}
                aria-describedby={ariaDescribedBy}
                aria-invalid={fieldState.error ? "true" : "false"}
                aria-required={required}
                onBlur={field.onBlur}
                className={autocompleteClass}
              />
            </FormControl>

            {description && (
              <FormDescription
                id={descriptionId}
                className={cn("text-muted-foreground text-sm", descriptionClass)}
              >
                {description}
              </FormDescription>
            )}

            {/* id always present so aria-describedby can reference it before an error appears */}
            <FormMessage id={errorId} className={cn("text-sm", messageClass)} />
          </FormItem>
        );
      }}
    />
  );
}

export default AsyncAutocompleteSingleSelectField;
